(() => {
  "use strict";

  const keys = ["description", "highlights", "fit", "materials"];
  const allowedTags = new Set([
    "P", "BR", "UL", "OL", "LI", "STRONG", "B", "EM", "I", "U", "S", "A", "IMG",
    "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "DL", "DT", "DD",
    "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TH", "TD", "CAPTION", "HR", "SPAN"
  ]);
  const materialPattern = /\b(?:cotton|polyester|elastane|spandex|nylon|linen|wool|viscose|rayon|gsm|fabric weight)\b|\d+\s*g\s*\/\s*m[²2]/i;
  const carePattern = /\b(?:wash(?:ing)?|detergents?|bleach|tumble|dry clean|iron(?:ing)?|steam|soak(?:ing)?|care instructions)\b/i;
  const fitPattern = /\b(?:oversized (?:silhouette|fit)|relaxed fit|slim fit|regular fit|true[ -]to[ -]size|size (?:up|down)|sizing|size guide)\b/i;
  const featurePattern = /\b(?:kangaroo pockets?|drop shoulders?|side[ -]seamed|double[ -]stitched|ribbed cuffs?|binding[ -]taped)\b/i;
  const hasContent = (node) => Boolean(node.textContent.trim() || node.querySelector("img,table"));

  function sanitize(html) {
    const parsed = new DOMParser().parseFromString(`<body>${typeof html === "string" ? html : ""}</body>`, "text/html");
    parsed.body.querySelectorAll("script,style,iframe,object,embed,form,input,button,textarea,select").forEach((element) => element.remove());
    Array.from(parsed.body.querySelectorAll("*")).forEach((element) => {
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(...element.childNodes);
        return;
      }
      const source = element.getAttribute(element.tagName === "A" ? "href" : "src");
      const alt = element.getAttribute("alt") || "";
      Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
      if (element.tagName === "A" || element.tagName === "IMG") {
        try {
          if (!source) throw new Error("missing-url");
          const url = new URL(source, window.location.origin);
          if (!["http:", "https:"].includes(url.protocol)) throw new Error("unsafe-url");
          element.setAttribute(element.tagName === "A" ? "href" : "src", url.href);
        } catch (_) {
          if (element.tagName === "IMG") element.replaceWith(document.createTextNode(alt));
        }
        if (element.tagName === "IMG") {
          element.setAttribute("alt", alt);
          element.setAttribute("loading", "lazy");
        }
      }
    });
    const container = document.createElement("div");
    container.append(...Array.from(parsed.body.childNodes).map((node) => document.importNode(node, true)));
    return container;
  }

  function headingKey(text) {
    const label = text.toLowerCase().replace(/&/g, "and").replace(/[:.\s]+$/g, "").replace(/\s+/g, " ").trim();
    if (/^(?:description|product description|overview|about this (?:product|item))$/.test(label) || /^why\b/.test(label)) return "description";
    if (/^(?:highlights|(?:key |product )?features|quality(?: and comfort)?|uncompromising quality, unmatched comfort)$/.test(label)) return "highlights";
    if (/^(?:fit(?: suggestion| notes| and sizing)?|sizing|size guide|size and fit)$/.test(label)) return "fit";
    if (/^(?:materials?(?: and (?:care|washing directions))?|fabric(?: and care| details)?|composition|care(?: instructions)?|washing(?: instructions| directions)?)$/.test(label)) return "materials";
    return null;
  }

  function classify(text) {
    if (carePattern.test(text)) return "materials";
    if (fitPattern.test(text)) return "fit";
    if (materialPattern.test(text)) return "materials";
    if (featurePattern.test(text)) return "highlights";
    return "description";
  }

  function split(html) {
    const source = sanitize(html);
    const sections = Object.fromEntries(keys.map((key) => [key, document.createElement("div")]));
    let active = null;
    const segmenter = typeof Intl.Segmenter === "function" ? new Intl.Segmenter("en", { granularity: "sentence" }) : null;

    // ponytail: conservative English heading/sentence rules; unknown formats remain in Description. Use explicit headings for new catalog formats.
    for (const node of Array.from(source.childNodes)) {
      const text = node.textContent.trim();
      if (!text && !(node.nodeType === 1 && node.matches("img,table,hr"))) continue;
      const element = node.nodeType === 1 ? node : null;
      const key = headingKey(text);
      const boldLabel = element?.matches("p") && element.children.length > 0
        && Array.from(element.children).every((child) => /^(STRONG|B)$/.test(child.tagName))
        && Array.from(element.children).map((child) => child.textContent).join("").trim() === text;
      const isHeading = element?.matches("h1,h2,h3,h4,h5,h6") || (boldLabel && text.length < 160 && !materialPattern.test(text));
      if (isHeading || (key && text.length < 100 && !element?.querySelector("br,ul,ol,table"))) {
        active = key;
        sections[key || "description"].append(node);
        continue;
      }

      if (element?.matches("ul,ol")) {
        for (const item of Array.from(element.children)) {
          const target = sections[active || classify(item.textContent)];
          let list = target.lastElementChild;
          if (list?.tagName !== element.tagName) {
            list = document.createElement(element.tagName.toLowerCase());
            target.append(list);
          }
          list.append(item);
        }
        continue;
      }

      const features = text.split("|").map((item) => item.trim()).filter(Boolean);
      if (element?.matches("p") && features.length > 2 && !element.querySelector("a,img")) {
        const list = document.createElement("ul");
        for (const feature of features) {
          const item = document.createElement("li");
          item.textContent = feature;
          list.append(item);
        }
        sections[active || (materialPattern.test(text) ? "materials" : "highlights")].append(list);
        continue;
      }

      if (!active && element?.matches("p") && !element.querySelector("a,img,br")) {
        const sentences = segmenter
          ? Array.from(segmenter.segment(text), (item) => item.segment.trim()).filter(Boolean)
          : text.split(/(?<=[.!?])\s+(?=[A-Z“"])/).filter(Boolean);
        const targets = sentences.map(classify);
        if (new Set(targets).size > 1) {
          let previous = null;
          let paragraph = null;
          sentences.forEach((sentence, index) => {
            if (targets[index] !== previous) {
              paragraph = document.createElement("p");
              sections[targets[index]].append(paragraph);
              previous = targets[index];
            }
            paragraph.append(document.createTextNode(`${paragraph.textContent ? " " : ""}${sentence}`));
          });
          continue;
        }
      }
      sections[active || classify(text)].append(node);
    }
    return sections;
  }

  window.JSCAProductDescription = { split, sanitize, hasContent };
})();
